import clsx from 'clsx';
import { useAbandonedBookings } from 'components/Bookings2/Abandoned/AbandonedBookingsProvider';
import styles from 'components/Bookings2/Abandoned/Card/AbandonedBookingCard.module.scss';
import Button from 'components/UI/V2/Button/Button';
import { useHasPremiumAccess } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { BookingAttempt } from 'model/BookingAttempt';
import React from 'react';
import { Link } from 'react-router-dom';
import { ClientsPath } from 'routes/Paths';
import { hashForUrl } from 'utils/HashUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/pro-light-svg-icons/faLocationDot';

interface AbandonedBookingCardProps {
    bookingAttempt: BookingAttempt;
    onSendSms?: (bookingAttempt: BookingAttempt) => void;
    onBook?: (bookingAttempt: BookingAttempt) => void;
}

export const AbandonedBookingCard: React.FC<AbandonedBookingCardProps> = ({ bookingAttempt, onBook, onSendSms }) => {
    const { getOrganizationMarketplace, organizationMarketplaces, isUserLoggedInMarketplace } =
        useOrganizationMarketplaces();
    const showMarketplaceName = organizationMarketplaces.length > 1;
    const allowActions = isUserLoggedInMarketplace(bookingAttempt.marketplaceId);

    const hasTwoWaySMSPremiumAccess = useHasPremiumAccess('two_way_sms');

    const { type, isLoading } = useAbandonedBookings();

    const clientPath = React.useMemo(() => {
        const hash = hashForUrl({ clientId: bookingAttempt.customerId, petId: undefined });

        return `${ClientsPath}/${hash}`;
    }, [bookingAttempt.customerId]);

    return (
        <div className={styles.root}>
            <div className={clsx(styles.header, styles.row)}>
                <Link
                    to={clientPath}
                    className={styles.customerLink}
                >
                    <p className={styles.name}>{bookingAttempt.name}</p>
                    <p className={styles.email}>{bookingAttempt.email}</p>
                </Link>

                <div>
                    <div className={styles.date}>{bookingAttempt.abandonedDate.format('MMM DD, hh:mm a')}</div>
                </div>
            </div>

            <div className={styles.row}>
                <div>
                    <h6>Type</h6>
                    <p>{bookingAttempt.type}</p>
                </div>
                <div>
                    <h6>Pets</h6>
                    <p>{bookingAttempt.pets}</p>
                </div>
            </div>

            <div className={styles.row}>
                <div>
                    <h6>Abandoned Step</h6>
                    <p>{bookingAttempt.abandonedStepName}</p>
                    {showMarketplaceName && (
                        <div className={styles.marketplaceContainer}>
                            <FontAwesomeIcon
                                icon={faLocationDot}
                                className={styles.marketplaceIcon}
                            />
                            <span className={styles.marketplaceName}>
                                {getOrganizationMarketplace(bookingAttempt.marketplaceId)?.name}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {allowActions && (
                <div className={styles.footer}>
                    {type !== 'converted' && (
                        <Button
                            label="Book"
                            variant="green"
                            isLoading={isLoading.get(bookingAttempt.id) ?? false}
                            onClick={() => onBook?.(bookingAttempt)}
                        />
                    )}
                    {hasTwoWaySMSPremiumAccess && (
                        <Button
                            label="Send SMS"
                            variant="black"
                            onClick={() => onSendSms?.(bookingAttempt)}
                        />
                    )}
                </div>
            )}
        </div>
    );
};
